import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminCourseDetail.scss';

const createEmptyHole = (
  number
) => ({
  hole_number: number,
  par: '',
  black_yards: '',
  gold_yards: '',
  blue_yards: '',
  white_yards: '',
  green_yards: '',
  red_yards: '',
  orange_yards: '',
  green_front_lat: '',
  green_front_lng: ''
});

export default function AdminCourseDetail() {
  const {
    id
  } = useParams();

  const navigate =
    useNavigate();

  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL ||
      '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    activeTab,
    setActiveTab
  ] = useState('overview');

  const [
    course,
    setCourse
  ] = useState(null);

  const [
    holes,
    setHoles
  ] = useState([]);

  const [
    pricing,
    setPricing
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState('');

  const [
    newPricing,
    setNewPricing
  ] = useState({
    price_category: '',
    time_range: '',
    price: ''
  });

  useEffect(() => {
    const fetchDetails =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/courses/${id}/details`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load course.'
            );
          }

          setCourse(
            data.course
          );

          const count =
            Number(
              data.course.num_holes
            ) || 18;

          const existingMap =
            new Map(
              (data.holes || []).map(
                (hole) => [
                  Number(
                    hole.hole_number
                  ),
                  hole
                ]
              )
            );

          const normalizedHoles =
            Array.from(
              {
                length: count
              },
              (_, index) => {
                const number =
                  index + 1;

                return {
                  ...createEmptyHole(
                    number
                  ),

                  ...(existingMap.get(
                    number
                  ) || {})
                };
              }
            );

          setHoles(
            normalizedHoles
          );

          setPricing(
            data.pricing || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load course.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchDetails();
  }, [
    apiBaseUrl,
    id,
    token
  ]);

  const handleHoleChange = (
    index,
    field,
    value
  ) => {
    setHoles((previous) =>
      previous.map(
        (hole, holeIndex) =>
          holeIndex === index
            ? {
                ...hole,
                [field]: value
              }
            : hole
      )
    );
  };

  const saveScorecard =
    async () => {
      setSaving(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/courses/${id}/holes`,
            {
              method: 'PUT',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body: JSON.stringify({
                holes
              })
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to save scorecard.'
          );
        }

        setHoles(
          data.holes || holes
        );

        setSuccess(
          'Scorecard saved successfully.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to save scorecard.'
        );
      } finally {
        setSaving(false);
      }
    };

  const handlePricingChange = (
    index,
    field,
    value
  ) => {
    setPricing((previous) =>
      previous.map(
        (item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                [field]: value
              }
            : item
      )
    );
  };

  const savePricingRow =
    async (item) => {
      setSaving(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/courses/${id}/pricing/${item.pricing_id}`,
            {
              method: 'PATCH',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body: JSON.stringify(
                item
              )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to update pricing.'
          );
        }

        setPricing(
          (previous) =>
            previous.map(
              (row) =>
                row.pricing_id ===
                data.pricing.pricing_id
                  ? data.pricing
                  : row
            )
        );

        setSuccess(
          'Pricing updated successfully.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to update pricing.'
        );
      } finally {
        setSaving(false);
      }
    };

  const addPricingRow =
    async () => {
      setSaving(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/courses/${id}/pricing`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body: JSON.stringify(
                newPricing
              )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to add pricing.'
          );
        }

        setPricing(
          (previous) => [
            ...previous,
            data.pricing
          ]
        );

        setNewPricing({
          price_category: '',
          time_range: '',
          price: ''
        });

        setSuccess(
          'Pricing added successfully.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to add pricing.'
        );
      } finally {
        setSaving(false);
      }
    };

  const deletePricingRow =
    async (pricingId) => {
      setSaving(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/courses/${id}/pricing/${pricingId}`,
            {
              method: 'DELETE',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to delete pricing.'
          );
        }

        setPricing(
          (previous) =>
            previous.filter(
              (item) =>
                item.pricing_id !==
                pricingId
            )
        );

        setSuccess(
          'Pricing deleted.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to delete pricing.'
        );
      } finally {
        setSaving(false);
      }
    };

  if (loading) {
    return (
      <div className="admin-course-detail-loading">
        Loading course...
      </div>
    );
  }

  if (!course) {
    return (
      <div className="admin-course-detail-loading">
        Course not found.
      </div>
    );
  }

  return (
    <div className="admin-course-detail">
      <section className="admin-course-detail__heading">
        <div>
          <p className="admin-course-detail__eyebrow">
            Course Management
          </p>

          <h2>
            {course.course_name}
          </h2>

          <p>
            {course.city || '—'},{' '}
            {course.province || '—'}
            {' · '}
            {course.num_holes ||
              '—'}{' '}
            holes
          </p>
        </div>

        <div className="admin-course-detail__heading-actions">
          <Link
            to={`/admin/courses/${id}/edit`}
            className="admin-course-detail__secondary-button"
          >
            Edit Overview
          </Link>

          <button
            type="button"
            className="admin-course-detail__secondary-button"
            onClick={() =>
              navigate(
                '/admin/courses'
              )
            }
          >
            Back to Courses
          </button>
        </div>
      </section>

      <nav className="admin-course-tabs">
        <button
          type="button"
          className={
            activeTab === 'overview'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'overview'
            )
          }
        >
          Overview
        </button>

        <button
          type="button"
          className={
            activeTab === 'scorecard'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'scorecard'
            )
          }
        >
          Scorecard
        </button>

        <button
          type="button"
          className={
            activeTab === 'pricing'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'pricing'
            )
          }
        >
          Pricing
        </button>
      </nav>

      {error ? (
        <div className="admin-course-detail__error">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="admin-course-detail__success">
          {success}
        </div>
      ) : null}

      {activeTab ===
      'overview' ? (
        <section className="admin-course-detail-card">
          <div className="admin-course-detail-card__header">
            <h3>
              Course Overview
            </h3>
          </div>

          <div className="admin-course-overview-grid">
            <div>
              <span>
                Course ID
              </span>

              <strong>
                {course.course_id}
              </strong>
            </div>

            <div>
              <span>
                Holes
              </span>

              <strong>
                {course.num_holes ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                Access
              </span>

              <strong>
                {course.public_private ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                City
              </span>

              <strong>
                {course.city ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                Province
              </span>

              <strong>
                {course.province ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                Country
              </span>

              <strong>
                {course.country ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                GPS
              </span>

              <strong>
                {course.latitude &&
                course.longitude
                  ? 'Added'
                  : 'Missing'}
              </strong>
            </div>

            <div>
              <span>
                Website
              </span>

              <strong>
                {course.course_website_link
                  ? 'Added'
                  : 'Missing'}
              </strong>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab ===
      'scorecard' ? (
        <section className="admin-course-detail-card">
          <div className="admin-course-detail-card__header">
            <div>
              <h3>
                Scorecard
              </h3>

              <p>
                Enter par and tee
                yardages for each hole.
              </p>
            </div>

            <button
              type="button"
              className="admin-course-detail__primary-button"
              onClick={
                saveScorecard
              }
              disabled={
                saving
              }
            >
              {saving
                ? 'Saving...'
                : 'Save Scorecard'}
            </button>
          </div>

          <div className="admin-scorecard-wrapper">
            <table className="admin-scorecard-table">
              <thead>
                <tr>
                  <th>
                    Hole
                  </th>

                  <th>
                    Par
                  </th>

                  <th>
                    Black
                  </th>

                  <th>
                    Gold
                  </th>

                  <th>
                    Blue
                  </th>

                  <th>
                    White
                  </th>

                  <th>
                    Green
                  </th>

                  <th>
                    Red
                  </th>

                  <th>
                    Orange
                  </th>
                </tr>
              </thead>

              <tbody>
                {holes.map(
                  (hole, index) => (
                    <tr
                      key={
                        hole.hole_number
                      }
                    >
                      <td className="admin-scorecard-hole">
                        {
                          hole.hole_number
                        }
                      </td>

                      {[
                        'par',
                        'black_yards',
                        'gold_yards',
                        'blue_yards',
                        'white_yards',
                        'green_yards',
                        'red_yards',
                        'orange_yards'
                      ].map(
                        (field) => (
                          <td
                            key={field}
                          >
                            <input
                              type="number"
                              value={
                                hole[field] ??
                                ''
                              }
                              onChange={(
                                event
                              ) =>
                                handleHoleChange(
                                  index,
                                  field,
                                  event
                                    .target
                                    .value
                                )
                              }
                            />
                          </td>
                        )
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab ===
      'pricing' ? (
        <section className="admin-course-detail-card">
          <div className="admin-course-detail-card__header">
            <div>
              <h3>
                Course Pricing
              </h3>

              <p>
                Manage public green-fee
                pricing and time ranges.
              </p>
            </div>
          </div>

          <div className="admin-pricing-list">
            {pricing.map(
              (item, index) => (
                <div
                  key={
                    item.pricing_id
                  }
                  className="admin-pricing-row"
                >
                  <input
                    type="text"
                    value={
                      item.price_category ||
                      ''
                    }
                    placeholder="Category"
                    onChange={(
                      event
                    ) =>
                      handlePricingChange(
                        index,
                        'price_category',
                        event.target
                          .value
                      )
                    }
                  />

                  <input
                    type="text"
                    value={
                      item.time_range ||
                      ''
                    }
                    placeholder="Time range"
                    onChange={(
                      event
                    ) =>
                      handlePricingChange(
                        index,
                        'time_range',
                        event.target
                          .value
                      )
                    }
                  />

                  <input
                    type="number"
                    step="0.01"
                    value={
                      item.price ??
                      ''
                    }
                    placeholder="Price"
                    onChange={(
                      event
                    ) =>
                      handlePricingChange(
                        index,
                        'price',
                        event.target
                          .value
                      )
                    }
                  />

                  <button
                    type="button"
                    onClick={() =>
                      savePricingRow(
                        item
                      )
                    }
                  >
                    Save
                  </button>

                  <button
                    type="button"
                    className="admin-pricing-row__delete"
                    onClick={() =>
                      deletePricingRow(
                        item.pricing_id
                      )
                    }
                  >
                    Delete
                  </button>
                </div>
              )
            )}

            <div className="admin-pricing-row admin-pricing-row--new">
              <input
                type="text"
                value={
                  newPricing.price_category
                }
                placeholder="e.g. Weekend"
                onChange={(
                  event
                ) =>
                  setNewPricing(
                    (previous) => ({
                      ...previous,

                      price_category:
                        event.target
                          .value
                    })
                  )
                }
              />

              <input
                type="text"
                value={
                  newPricing.time_range
                }
                placeholder="e.g. Before 12 PM"
                onChange={(
                  event
                ) =>
                  setNewPricing(
                    (previous) => ({
                      ...previous,

                      time_range:
                        event.target
                          .value
                    })
                  )
                }
              />

              <input
                type="number"
                step="0.01"
                value={
                  newPricing.price
                }
                placeholder="Price"
                onChange={(
                  event
                ) =>
                  setNewPricing(
                    (previous) => ({
                      ...previous,

                      price:
                        event.target
                          .value
                    })
                  )
                }
              />

              <button
                type="button"
                className="admin-course-detail__primary-button"
                onClick={
                  addPricingRow
                }
                disabled={
                  saving
                }
              >
                + Add
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}